import React, { useEffect, useState } from "react";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import SearchIcon from "@mui/icons-material/Search";
import { Pencil, Trash2 } from "lucide-react";
import { LicenseTranslation } from "../../Translations/LicenseTranslation";
import { Language, License } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GeneralTranslation } from "../../Translations/GeneralTranslation";
import { useSelector } from "react-redux";
import { RootState } from "../../components/Manual_List/fetch/store";
import CircularProgress from "@mui/material/CircularProgress";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import {
  deleteLicense,
  fetchLicenses,
} from "../../components/Manual_List/fetch/slices";
import { useAppDispatch } from "../../components/Manual_List/fetch/hook";
import { useNavigate } from "react-router-dom";

const LicensesList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const licenses: License[] = useSelector(
    (state: RootState) => state.data.licenses.data
  );

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const { language } = useLanguage();
  const gt = GeneralTranslation[language as Language];
  const t = LicenseTranslation[language as Language];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    setLoading(true);
    dispatch(fetchLicenses());
    setLoading(false);
  }, [dispatch]);

  const openConfirmationModal = (license: License) => {
    setLicenseToDelete(license);
    setIsConfirmationModalOpen(true);
  };

  const handleDeleteLicense = (id: number) => {
    dispatch(deleteLicense(id));
    setIsConfirmationModalOpen(false);
  };

  const filteredLicenses = licenses.filter((license) => {
    return (
      searchTerm === "" ||
      license.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      license.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const datetimeFormat = (datetimeString: any) => {
    if (!datetimeString) return "";

    const date = new Date(datetimeString.date);
    return date.toLocaleString();
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />

      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col">
          <h1 className="text-2xl font-semibold mb-6">{t.title}</h1>

          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "flex-col space-y-4 w-full"
                : "justify-between items-center"
            } mb-6`}
          >
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder={gt.search}
                className="px-4 py-3 border border-gray-300 rounded-lg w-full pr-12 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.currentTarget.value)}
              />
              <SearchIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
            <button
              className={`bg-purple text-white ${
                isSmallScreen || isMediumScreen
                  ? "px-4 py-2.5 w-full"
                  : "px-6 py-2.5 w-48"
              } rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all`}
              onClick={() => navigate("/add-license")}
            >
              {t.addLicense}
            </button>
          </div>

          {filteredLicenses.length > 0 && (
            <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-darkSecondaryBackground border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {gt.name}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.email}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.numberOfUsers}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.createdAt}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.validUntil}
                    </th>
                    <th className="py-4 px-6 font-semibold text-right text-sm text-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-darkSecondaryBackground divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLicenses.map((license) => (
                    <tr
                      key={license.id}
                      className="group hover:bg-purple hover:bg-opacity-5 dark:hover:bg-purple dark:hover:bg-opacity-10 transition-all duration-200 cursor-pointer"
                    >
                      <td className="py-4 px-6 text-left text-gray-900 dark:text-gray-100 font-medium">
                        {license.name}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        {license.email}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        {license.numberOfAccounts}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        {datetimeFormat(license.createdAt)}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        {datetimeFormat(license.expiredAt)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/add-license/${license.id}`);
                            }}
                            className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/edit"
                            title="Edit license"
                          >
                            <Pencil
                              className="text-gray-400 dark:text-gray-500 group-hover/edit:text-blue-600 dark:group-hover/edit:text-blue-400 group-hover/edit:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmationModal(license);
                            }}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/delete"
                            title="Delete license"
                          >
                            <Trash2
                              className="text-gray-400 dark:text-gray-500 group-hover/delete:text-red-500 dark:group-hover/delete:text-red-400 group-hover/delete:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filteredLicenses.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-darkSecondaryBackground rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-4 text-lg font-medium text-gray-500 dark:text-gray-400">
                No licenses found
              </p>
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                Try adjusting your search or add a new license
              </p>
            </div>
          )}
        </div>
        {loading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
            <CircularProgress />
          </div>
        )}
      </div>
      {licenseToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => handleDeleteLicense(licenseToDelete.id)}
          label={t.deleteLicenseConfirmation.replace(
            "{label}",
            licenseToDelete.name
          )}
        />
      )}
    </div>
  );
};

export default LicensesList;
